import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { app } from '../firebaseConfig';
import { getCountries } from '../components/cart/Utils';
import { generateOrder, generateEnviaOrder } from './api/Label';
import CartItems from './CartItem';
import CartSummary from './CartSummary';
import AddressStep from './AddressStep';
import PaymentStep from './PaymentStep';
import PaymentInterface from './PaymentInterface';
import Login from './Login'; // Import the Login component
import { ArrowLeft } from 'lucide-react';
import { Address_ToDB, Address_FromDB } from './cart/address_storage';
import FooterSection from './landing/FooterSection';

const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();

const CartScreen = ({ cart, setCart, setScreen, updateCartQuantity }) => {
  const [user, setUser] = useState(auth.currentUser);
  const [orderStatus, setOrderStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedAddress, setSavedAddress] = useState(null);

  const [orderId, setOrderId] = useState()
  const [address, setAddress] = useState({
    fullName: '',
    phone: '',
    street: '',
    number: '',
    district: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'IN',
    email: '',
    reference: '',
    carrier: 'ecomexpress',
    shippingService: 'exs',
  });
  const [selectedAddress, setSelectedAddress] = useState('new');
  const [countries, setCountries] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('prepaid');
  const [showPaymentInterface, setShowPaymentInterface] = useState(false);
  const [step, setStep] = useState(1);
  const [isLoginOpen, setIsLoginOpen] = useState(false); // State for Login popup

  const normalizedCart = useMemo(() =>
    cart.map(item => ({
      ...item,
      id: item.id || `${item.name}-${item.size}-${item.color}-${Date.now()}`,
      content: `${item.name} - ${item.size}, ${item.color}`,
      declaredValue: item.price || 0,
      quantity: item.quantity || 1,
    })),
    [cart]
  );

  const { cartItems, subtotal, shippingCost, totalPrice } = useMemo(() => {
    const grouped = normalizedCart.reduce((acc, item) => {
      const key = `${item.name}-${item.size}-${item.color}`;
      if (!acc[key]) {
        acc[key] = { ...item, quantity: 0 };
      }
      acc[key].quantity += item.quantity;
      acc[key].declaredValue = (item.price || 0) * acc[key].quantity;
      acc[key].content = `${item.name} - ${item.size}, ${item.color}`;
      return acc;
    }, {});

    const items = Object.values(grouped);
    const sub = items.reduce((acc, item) => acc + item.declaredValue, 0);
    const shipping = sub > 0 ? 0 : 0;
    return {
      cartItems: items,
      subtotal: sub,
      shippingCost: shipping,
      totalPrice: sub + shipping,
    };
  }, [normalizedCart]);

  const currency = 'INR';

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(currentUser => {
      setUser(currentUser);
      if (currentUser) {
        fetchSavedAddress();
        setIsLoginOpen(false); // Close login popup on successful auth
        // setStep(2); // Proceed to address step
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchInitialData = useCallback(async () => {
    try {
      const countryList = await getCountries();
      setCountries(countryList.data || countryList || []);
      if (user) {
        await fetchSavedAddress();
        setAddress(prev => ({ ...prev, email: user.email }));
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const fetchSavedAddress = async () => {
    const address_from_db = Address_FromDB();
    setSavedAddress(address_from_db);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      setUser(result.user);
      await fetchSavedAddress();
      setAddress(prev => ({ ...prev, email: result.user.email }));
      setIsLoginOpen(false); // Close the login popup
      setStep(2); // Proceed to address step
    } catch (error) {
      console.error('Google Sign-In failed:', error);
      alert('Failed to sign in with Google.');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = () => {
    if (user) {
      setStep(2); // Proceed to address step if user is authenticated
    } else {
      setIsLoginOpen(true); // Show login popup if no user
    }
  };

  const processCheckout = async () => {
    if (!user) {
      setIsLoginOpen(true); // Show login popup if user is not authenticated
      return;
    }

    const finalAddress = selectedAddress === 'saved' && savedAddress ? savedAddress : address;
    const requiredFields = ['fullName', 'phone', 'street', 'number', 'district', 'city', 'postalCode'];

    if (requiredFields.some(field => !finalAddress[field])) {
      alert('Please fill in all required address fields.');
      return;
    }

    setLoading(true);
    Address_ToDB(finalAddress);
    try {
      const orderDetails = {
        userEmail: user.email,
        items: cartItems,
        address: finalAddress,
        totalPrice: totalPrice,
        paymentMethod: paymentMethod,
      };

      if (paymentMethod === 'prepaid') {
        setShowPaymentInterface(true);
      } else {
        const orderResult = await generateOrder(orderDetails);
        if (!orderResult.success) throw new Error(orderResult.error?.message || 'Order generation failed');
        setOrderStatus('success');
        setTimeout(() => {
          setOrderStatus(null);
          setCart([]);
          setScreen('/orders');
        }, 2000);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setOrderStatus('failed');
      alert(`Checkout failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };


  const handlePaymentComplete = success => {
    setShowPaymentInterface(false);
    const finalAddress = selectedAddress === 'saved' && savedAddress ? savedAddress : address;
    const orderDetails = {
      userEmail: user.email,
      items: cartItems,
      address: finalAddress,
      totalPrice: totalPrice,
      paymentMethod: paymentMethod,
    };

    setOrderStatus(success ? 'success' : 'failed');

    if (success) {
      const orderResult = generateOrder(orderDetails);
      setTimeout(() => {
        setOrderStatus(null);
        setCart([]);
        setScreen('/orders');
      }, 2000);
    }
  };

  const updateQuantity = (itemKey, delta) => {
    const newQuantity = itemKey.quantity + delta;
    updateCartQuantity(itemKey.id, newQuantity);
  };

  const removeItem = (itemKey) => {
    setCart(prev => prev.filter(item => `${item.name}-${item.size}-${item.color}` !== itemKey));
  };

  const finalAddress = selectedAddress === 'saved' && savedAddress ? savedAddress : address;

  return (
    <div className="p-6 max-w-4xl mx-auto bg-primary dark:bg-primary-dark min-h-screen text-white">
      <button
        onClick={() => setScreen('/shop')}
        className="mb-6 flex items-center text-secondary dark:text-secondary hover:text-[#DBAE6E] transition-colors"
      >
        <ArrowLeft size={24} /> <span className="ml-2">Back</span>
      </button>

      <h2 className="text-3xl font-bold text-[#DBAE6E] mb-8">
        {step === 1 ? 'Your Cart' : step === 2 ? 'Shipping Address' : 'Payment'}
      </h2>

      {step === 1 && (
        <>
          <CartItems
            cartItems={cartItems}
            updateQuantity={updateQuantity}
            removeItem={removeItem}
          />
          <CartSummary
            subtotal={subtotal}
            shippingCost={shippingCost}
            totalPrice={totalPrice}
            currency={currency}
            setScreen={setScreen}
            handleBuyNow={handleBuyNow}
            loading={loading}
            cartItems={cartItems}
          />
        </>
      )}

      {step === 2 && (
        <AddressStep
          address={address}
          setAddress={setAddress}
          savedAddress={savedAddress}
          selectedAddress={selectedAddress}
          setSelectedAddress={setSelectedAddress}
          countries={countries}
          cartItems={cartItems}
          totalPrice={totalPrice}
          setStep={setStep}
          step={step}
        />
      )}

      {step === 3 && (
        <PaymentStep
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          subtotal={subtotal}
          shippingCost={shippingCost}
          totalPrice={totalPrice}
          currency={currency}
          processCheckout={processCheckout}
          loading={loading}
          orderStatus={orderStatus}
          setStep={setStep}
        />
      )}

      {showPaymentInterface && (
        <PaymentInterface
          total={totalPrice}
          currency={currency}
          cartItems={cartItems}
          address={finalAddress}
          orderID={`${Date.now()}`}
          name={finalAddress.fullName || user?.displayName || 'Customer'}
          email={finalAddress.email || user?.email || ''}
          phone={finalAddress.phone || ''}
          onClose={() => setShowPaymentInterface(false)}
          onPay={handlePaymentComplete}
        />
      )}

      <Login
        isOpen={isLoginOpen}
        setIsOpen={setIsLoginOpen}
      />

      <FooterSection />
    </div>
  );
};

export default CartScreen;